/**
 * Ortak Öğrenci Soru Görünümü — StudentQuestionRenderer
 *
 * Bu bileşen hem Merkez (ehliyetdersi.net) hem de OtomasyonHub'da
 * birebir aynı içerikte bulunmalıdır.
 *
 * KATÎ KURAL: Bu bileşen hiçbir API yazma çağrısı (PUT/POST/DELETE) yapmaz.
 *             Tüm durum yalnızca React component state'inde tutulur.
 *             Doğru cevap, kullanıcı bir şık seçmeden görünmez.
 *
 * Props:
 *   question       {Object|null}  adaptCenterQuestion() çıktısı (ortak model)
 *   trialCandidate {Object|null}  { playback_url, media_type, usage_key, option_key? }
 *                                  Adım 5'te QuestionDetail tarafından sağlanır.
 *                                  null ise medyada herhangi bir değişiklik olmaz.
 *   onRemoveTrial  {Function|null} Aday kaldırıldığında çağrılır — önceki senaryoya döner.
 */

import { useState, useEffect } from "react";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import { BACKEND_URL_BASE } from "../services/api";

const OPTIONS = ['A', 'B', 'C', 'D'];

// ─── Video Proxy ──────────────────────────────────────────────────────────────
// .r2.dev URL'leri için backend proxy (Range request desteği).
// media.ehliyetdersi.net CDN URL'leri doğrudan sunulur — proxy gerekmez.
// Bu fonksiyon hiçbir ağ çağrısı başlatmaz; sadece URL dönüştürür.
function _videoProxySrc(url) {
  if (!url) return url;
  if (url.includes('.r2.dev/')) {
    return `${BACKEND_URL_BASE}/api/media/video-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
}

// ─── Video Player ─────────────────────────────────────────────────────────────
function VideoPlayer({ src }) {
  const [status, setStatus] = useState('loading');
  if (!src) return null;
  const proxied = _videoProxySrc(src);
  return (
    <div className="relative rounded-lg overflow-hidden bg-black" data-testid="sqr-video-player">
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
      {status === 'error' ? (
        <div className="flex items-center justify-center h-16 bg-red-950/20 border border-red-800/30 rounded-lg">
          <p className="text-red-400 text-xs">Video oynatılamadı</p>
        </div>
      ) : (
        <video
          key={proxied}
          src={proxied}
          controls
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="max-w-full max-h-64 block w-full"
          onLoadStart={() => setStatus('loading')}
          onCanPlay={() => setStatus('ready')}
          onError={() => setStatus('error')}
        />
      )}
    </div>
  );
}

// ─── Option Button ────────────────────────────────────────────────────────────
function OptionButton({ letter, text, imgUrl, correctAnswer, selectedLetter, answered, onClick, isFocused = false }) {
  const isCorrect  = correctAnswer === letter;
  const isSelected = selectedLetter === letter;

  let cls = 'w-full flex items-center gap-3 rounded-lg px-4 py-3 text-left transition-all border text-sm ';

  if (!answered) {
    cls += 'bg-white border-slate-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer';
  } else if (isSelected && isCorrect) {
    cls += 'bg-teal-500 border-teal-500 text-white cursor-default';
  } else if (isSelected && !isCorrect) {
    cls += 'bg-red-500 border-red-500 text-white cursor-default';
  } else if (isCorrect) {
    cls += 'bg-teal-500 border-teal-500 text-white cursor-default';
  } else {
    cls += 'bg-white border-slate-100 text-slate-400 cursor-default opacity-60';
  }

  // Şık aday deneme odağı: cevaplanmamışken mor halka
  if (isFocused && !answered) cls += ' ring-2 ring-violet-400 ring-offset-1';

  const icon = answered && (isSelected || isCorrect)
    ? (isCorrect
        ? <CheckCircle2 size={16} className="flex-shrink-0" />
        : <XCircle size={16} className="flex-shrink-0" />)
    : null;

  return (
    <button
      type="button"
      data-testid={`sqr-option-${letter}`}
      onClick={() => !answered && onClick(letter)}
      className={cls}
    >
      <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
        ${answered && (isSelected || isCorrect) ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-500'}`}>
        {letter}
      </span>
      <span className="flex-1 leading-snug">
        {imgUrl
          ? <img src={imgUrl} alt={`${letter} şık görseli`} className="max-h-16 max-w-full object-contain rounded" />
          : text
          ? <span dangerouslySetInnerHTML={{ __html: text }} />
          : <span className="italic text-current/50">Boş şık</span>
        }
      </span>
      {icon}
    </button>
  );
}

// ─── Trial Candidate Media Patch (local-only, no API call) ────────────────────
// Ana soru medya türleri (primary_visual, prompt_video, gif, animation) birbirini bastırır.
// Açıklama adayı açıklama bölümünü etkiler. Şık adayı ilgili şık görselini değiştirir.
// Geri bildirim adayları feedback bölümünü etkiler.
// Bu fonksiyon hiçbir ağ çağrısı yapmaz; sadece yerel medya objesini günceller.
const _QUESTION_MAIN_KEYS = ['question.primary_visual', 'question.prompt_video', 'question.gif', 'question.animation'];

// Referans ortak modelden gelen mediaRef nesnesini ({ url, kind, ... }) veya
// düz URL string'ini alır; oynatılabilir URL string döndürür.
// _applyTrialCandidate sonrası URL string, adaptCenterQuestion sonrası mediaRef nesnesi gelebilir.
function _url(ref) {
  if (!ref) return null;
  if (typeof ref === 'string') return ref;
  return ref.url ?? null;
}

// optionFeedback değerinden oynatılabilir URL çıkarır.
// Referans modeli: [] (boş dizi), URL string, mediaRef nesnesi veya null gelebilir.
// Yalnızca ilk geçerli eleman kullanılır — liste formatı da güvenle işlenir.
function _optFeedbackUrl(val) {
  if (!val) return null;
  if (Array.isArray(val)) {
    const first = val[0];
    return first ? _url(first) : null;
  }
  return _url(val);
}

function _applyTrialCandidate(media, candidate) {
  if (!candidate || !candidate.playback_url || !candidate.usage_key) return media;
  // Derin kopya — orijinal medya objesini kesinlikle değiştirme
  const m = JSON.parse(JSON.stringify(media));
  const { usage_key, option_key, playback_url } = candidate;

  // Ana soru medya türleri birbirini bastırır (suppression)
  if (_QUESTION_MAIN_KEYS.includes(usage_key)) {
    m.question.primaryVisual = null;
    m.question.promptVideo   = null;
    m.question.gif           = null;
    m.question.animation     = null;
  }

  if      (usage_key === 'question.primary_visual')         m.question.primaryVisual = playback_url;
  else if (usage_key === 'question.prompt_video')           m.question.promptVideo   = playback_url;
  else if (usage_key === 'question.gif')                    m.question.gif           = playback_url;
  else if (usage_key === 'question.animation')              m.question.animation     = playback_url;
  else if (usage_key === 'option.visual' && option_key) {
    if (m.options[option_key]) m.options[option_key].visual = playback_url;
  } else if (usage_key === 'option.feedback' && option_key) {
    // Şık bazlı geri bildirim: media.optionFeedback[A/B/C/D] → playback_url
    if (!m.optionFeedback) m.optionFeedback = {};
    m.optionFeedback[option_key] = playback_url;
  } else if (usage_key === 'question.explanation_video')    m.explanation.video      = playback_url;
  else if (usage_key === 'question.explanation_visual')     m.explanation.visual     = playback_url;
  else if (usage_key === 'question.correct_feedback_video') {
    if (!m.feedback) m.feedback = {};
    m.feedback.correctVideo = playback_url;
  } else if (usage_key === 'question.wrong_feedback_video') {
    if (!m.feedback) m.feedback = {};
    m.feedback.wrongVideo = playback_url;
  }

  return m;
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────
export default function StudentQuestionRenderer({ question, trialCandidate = null, onRemoveTrial = null }) {
  const [selected,              setSelected]              = useState(null);
  const [answered,              setAnswered]              = useState(false);
  const [showExplanation,       setShowExplanation]       = useState(false);
  // Adım 5: açıklama/geri bildirim adayı → açıklama bölümünü zorla aç
  const [forceShowExplanation,  setForceShowExplanation]  = useState(false);
  // Adım 5: şık görsel adayı için odak halkası
  const [trialFocusedOption,    setTrialFocusedOption]    = useState(null);
  // P0-3: şık feedback adayı → görünüm amaçlı otomatik seçim (gerçek state değişmez)
  const [trialSelection,        setTrialSelection]        = useState(null); // { selected, answered }

  // trialCandidate değişince otomatik senaryo tetikle (P0-3)
  useEffect(() => {
    if (!trialCandidate) {
      setForceShowExplanation(false);
      setTrialFocusedOption(null);
      setTrialSelection(null);
      return;
    }
    const uk = trialCandidate.usage_key;
    const ok = trialCandidate.option_key;
    if (uk === 'question.explanation_video' || uk === 'question.explanation_visual') {
      setForceShowExplanation(true);
      setTrialFocusedOption(null);
      setTrialSelection(null);
    } else if (uk === 'option.visual' && ok) {
      setForceShowExplanation(true);   // geri bildirim bölümünü de aç
      setTrialFocusedOption(ok);
      // Şık otomatik seçilir (görünüm amaçlı — gerçek selected/answered state korunur)
      setTrialSelection({ selected: ok, answered: true });
    } else if (uk === 'option.feedback' && ok) {
      // Şık geri bildirimi adayı: ilgili şık otomatik seçili+cevaplanmış gösterilir
      // (post-answer bölümünde feedback oynar — forceShow gerekmez)
      setForceShowExplanation(false);
      setTrialFocusedOption(ok);
      setTrialSelection({ selected: ok, answered: true });
    } else if (uk === 'question.correct_feedback_video' || uk === 'question.wrong_feedback_video') {
      setForceShowExplanation(true);
      setTrialFocusedOption(null);
      setTrialSelection(null);
    } else {
      setForceShowExplanation(false);
      setTrialFocusedOption(null);
      setTrialSelection(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialCandidate?.usage_key, trialCandidate?.option_key]);

  if (!question) {
    return (
      <div className="flex items-center justify-center h-32 text-slate-400 text-sm"
        data-testid="sqr-empty">
        Soru verisi yükleniyor...
      </div>
    );
  }

  const { content, media } = question;
  const correctAnswer = content.correctAnswer;

  // trialCandidate sadece yerel state'e uygulanır — API çağrısı YOK
  const effectiveMedia = trialCandidate
    ? _applyTrialCandidate(media, trialCandidate)
    : media;

  // P0-3: trialSelection görünüm amaçlı selected/answered override (gerçek state korunur)
  const displaySelected = trialSelection?.selected ?? selected;
  const displayAnswered = trialSelection?.answered ?? answered;

  function handleOptionClick(letter) {
    setSelected(letter);
    setAnswered(true);
    setShowExplanation(false);
    setTrialSelection(null); // kullanıcı tıklayınca trial selection temizlenir
  }

  function handleReset() {
    setSelected(null);
    setAnswered(false);
    setShowExplanation(false);
    setTrialSelection(null);
  }

  const isCorrect = displayAnswered && displaySelected === correctAnswer;
  const hasExplanation = !!(
    content.explanationHtml ||
    effectiveMedia.explanation?.video ||
    effectiveMedia.explanation?.visual
  );

  // Birincil soru medyası: video > gif > görsel önceliği
  // _url: referans modelin mediaRef nesnesini ({url,...}) veya düz URL'i işler
  const questionVideo = _url(effectiveMedia.question.promptVideo) || _url(effectiveMedia.question.animation);
  const questionImage = _url(effectiveMedia.question.primaryVisual);
  const questionGif   = _url(effectiveMedia.question.gif);

  return (
    <div className="space-y-4" data-testid="student-question-renderer">

      {/* ── Deneme Adayı Bandı (Adım 5) ── */}
      {trialCandidate && (
        <div
          data-testid="sqr-trial-banner"
          className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700"
        >
          <div className="flex-1 min-w-0">
            <span className="font-semibold">Aday medya deneniyor</span>
            <span className="text-amber-600 ml-1">— Yayın ve soru kaydı değişmedi</span>
          </div>
          <button
            type="button"
            data-testid="sqr-trial-remove-btn"
            onClick={() => onRemoveTrial?.()}
            className="shrink-0 text-[10px] px-2 py-1 border border-amber-300 text-amber-700 rounded hover:bg-amber-100 transition-colors"
          >
            Kaldır
          </button>
        </div>
      )}

      {/* ── Soru Medyası ── */}
      {questionVideo ? (
        <VideoPlayer src={questionVideo} />
      ) : questionGif ? (
        <img
          src={questionGif}
          alt="Soru GIF"
          className="max-w-full max-h-48 object-contain rounded-lg"
          data-testid="sqr-question-gif"
        />
      ) : questionImage ? (
        <img
          src={questionImage}
          alt="Soru görseli"
          className="max-w-full max-h-48 object-contain rounded-lg"
          data-testid="sqr-question-image"
        />
      ) : null}

      {/* ── Soru Metni ── */}
      {content.questionHtml ? (
        <div
          data-testid="sqr-question-text"
          className="text-slate-800 text-sm font-medium leading-relaxed"
          dangerouslySetInnerHTML={{ __html: content.questionHtml }}
        />
      ) : (
        <p className="text-slate-400 text-xs italic" data-testid="sqr-question-empty">
          Soru metni girilmemiş
        </p>
      )}

      {/* ── Şıklar ── */}
      <div className="space-y-2" data-testid="sqr-options">
        {OPTIONS.map(letter => (
          <OptionButton
            key={letter}
            letter={letter}
            text={content.options[letter]}
            imgUrl={_url(effectiveMedia.options[letter]?.visual)}
            correctAnswer={correctAnswer}
            selectedLetter={displaySelected}
            answered={displayAnswered}
            onClick={handleOptionClick}
            isFocused={trialFocusedOption === letter}
          />
        ))}
      </div>

      {/* ── Açıklama/Geri Bildirim Önizlemesi (forceShowExplanation — P0-3/Adım 5) ── */}
      {forceShowExplanation && !displayAnswered && (
        <div
          className="bg-slate-50 rounded-lg p-3 border border-violet-200 space-y-3"
          data-testid="sqr-trial-explanation-preview"
        >
          <p className="text-[10px] font-semibold text-violet-600 uppercase tracking-wide">
            Açıklama / Geri Bildirim Önizlemesi (Aday Medya)
          </p>
          {_url(effectiveMedia.explanation?.video) && (
            <VideoPlayer src={_url(effectiveMedia.explanation.video)} />
          )}
          {_url(effectiveMedia.explanation?.visual) && (
            <img
              src={_url(effectiveMedia.explanation.visual)}
              alt="Açıklama görseli"
              className="max-w-full max-h-48 object-contain rounded"
            />
          )}
          {effectiveMedia.feedback?.correctVideo && (
            <div>
              <p className="text-[9px] text-teal-600 font-semibold mb-1">Doğru Cevap Videosu</p>
              <VideoPlayer src={effectiveMedia.feedback.correctVideo} />
            </div>
          )}
          {effectiveMedia.feedback?.wrongVideo && (
            <div>
              <p className="text-[9px] text-red-600 font-semibold mb-1">Yanlış Cevap Videosu</p>
              <VideoPlayer src={effectiveMedia.feedback.wrongVideo} />
            </div>
          )}
          {content.explanationHtml && (
            <div
              className="text-slate-700 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: content.explanationHtml }}
            />
          )}
          {!effectiveMedia.explanation?.video && !effectiveMedia.explanation?.visual &&
           !effectiveMedia.feedback?.correctVideo && !effectiveMedia.feedback?.wrongVideo &&
           !content.explanationHtml && (
            <p className="text-[10px] text-slate-400 italic">Açıklama içeriği girilmemiş</p>
          )}
        </div>
      )}

      {/* ── Cevap Sonrası ── */}
      {displayAnswered && (
        <div className="space-y-3" data-testid="sqr-feedback">
          {/* Doğru/Yanlış bant */}
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-semibold
            ${isCorrect
              ? 'bg-teal-50 border-teal-200 text-teal-700'
              : 'bg-red-50  border-red-200  text-red-700'}`}
            data-testid={isCorrect ? 'sqr-feedback-correct' : 'sqr-feedback-incorrect'}
          >
            {isCorrect
              ? <CheckCircle2 size={16} className="flex-shrink-0" />
              : <XCircle     size={16} className="flex-shrink-0" />}
            {isCorrect
              ? 'Doğru!'
              : `Yanlış — Doğru cevap: ${correctAnswer ?? '?'}`}
          </div>

          {/* Şık Geri Bildirimi — yalnızca seçilen şık oynar; diğer şıklar gösterilmez */}
          {displaySelected && _optFeedbackUrl(effectiveMedia.optionFeedback?.[displaySelected]) && (
            <div
              data-testid={`sqr-option-feedback-${displaySelected}`}
              className="space-y-1.5"
            >
              <p className="text-[9px] text-violet-600 font-semibold uppercase tracking-wide">
                Şık {displaySelected} Geri Bildirimi
              </p>
              <VideoPlayer src={_optFeedbackUrl(effectiveMedia.optionFeedback[displaySelected])} />
            </div>
          )}

          {/* Açıklama toggle */}
          {hasExplanation && (
            <button
              type="button"
              data-testid="sqr-explanation-toggle"
              onClick={() => setShowExplanation(v => !v)}
              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors"
            >
              {showExplanation ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showExplanation ? 'Açıklamayı Gizle' : 'Açıklamayı Göster'}
            </button>
          )}

          {/* Açıklama içeriği */}
          {showExplanation && (
            <div
              className="bg-slate-50 rounded-lg p-3 border border-slate-200 space-y-3"
              data-testid="sqr-explanation"
            >
              {_url(effectiveMedia.explanation?.video) && (
                <VideoPlayer src={_url(effectiveMedia.explanation.video)} />
              )}
              {_url(effectiveMedia.explanation?.visual) && (
                <img
                  src={_url(effectiveMedia.explanation.visual)}
                  alt="Açıklama görseli"
                  className="max-w-full max-h-48 object-contain rounded"
                />
              )}
              {content.explanationHtml && (
                <div
                  className="text-slate-700 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: content.explanationHtml }}
                />
              )}
            </div>
          )}

          {/* Tekrar dene */}
          <button
            type="button"
            data-testid="sqr-reset-btn"
            onClick={handleReset}
            className="text-xs text-slate-400 hover:text-slate-600 underline transition-colors"
          >
            Sıfırla
          </button>
        </div>
      )}
    </div>
  );
}
